import { strToU8, zipSync } from 'fflate';

import { triggerBlobDownload } from '#core/lib/utils/trigger-blob-download.ts';

export interface NamedEntry {
  readonly name: string;
  readonly csv: string;
}
interface DisambiguatedEntry extends NamedEntry {
  readonly uniqueName: string;
}

/**
 * Disambiguate colliding names by counting prior occurrences of each
 * name within the input. O(n²) on the prefix lookup, which is fine at
 * dashboard-tile scale (single-digit to low-double-digit entries) — and
 * cleanly functional: pure `map`, no accumulator spread, no mutation.
 */
function uniqueNames(entries: readonly NamedEntry[]): readonly DisambiguatedEntry[] {
  return entries.map((entry, i) => {
    const occurrences = entries.slice(0, i).filter((e) => e.name === entry.name).length;
    return { ...entry, uniqueName: occurrences === 0 ? entry.name : `${entry.name} (${occurrences + 1})` };
  });
}

/**
 * Bundle a list of named CSV payloads into a ZIP archive and trigger a
 * browser file-save as `<fileName>.zip`. SSOT for the "download these
 * CSVs as one archive" operation: every entry is encoded as UTF-8 and
 * stored under `${name}.csv`; colliding `name` values get parenthesised
 * disambiguators in input order (`foo.csv`, `foo (2).csv`, …) so no
 * payload is lost to the ZIP central-directory's unique-path rule.
 */
export function downloadCsvZip(entries: readonly NamedEntry[], fileName: string): void {
  const archive = zipSync(
    Object.fromEntries(uniqueNames(entries).map(({ uniqueName, csv }) => [`${uniqueName}.csv`, strToU8(csv)])),
  );
  triggerBlobDownload(archive, `${fileName}.zip`, 'application/zip');
}
